// Vendor Libs
import {
    filter,
    findIndex,
    first,
    get,
    includes,
    isArray,
    isEmpty,
    map,
    pick,
    pickBy,
    set,
    size,
    sortBy,
    trim,
} from 'lodash';
import {batch} from 'react-redux';
import {push} from 'redux-first-history';

// Data
import actionsServiceCaller from '../lib/app/actions-service-caller';
import echostarContentTypeMap from '../bootstrap-data/echostar-content-type-map';

// Configs
import servicesFormConfig from '../../config/services-form-config';

// Core Libs
import {formatDate, formatDuration, parseDurationAs} from '../lib/core/date';

const mappingTabNames = ['BESTMATCHES', 'MOVIESANDPROGRAMSMAPPING', 'CREATESHELL'];

const mappingMiddleware = (store) => (next) => (action) => {
    const {dispatch, getState} = store;
    const state = getState();

    next(action);

    const nextState = getState();
    const actions = {
        FORM_REGISTER: () => handleFormRegister(dispatch, action.formName, nextState),
        FORM_SET_FIELD_VALUE: () => handleFormFieldUpdate(dispatch, action, nextState),
        FORM_SET_FIELD_VALUES: () => validateForm(dispatch, action, nextState),
        FORM_SUBMITTED: () => handleFormSubmitted(dispatch, action, nextState),
        FORM_UNREGISTER_FIELD_ERRORS: () => validateForm(dispatch, action, nextState),
        LAYOUT_UPDATE: () => handleLayoutUpdate(dispatch, action),
        MAPPING_CANCEL_MAPPING: () => cancelMapping(dispatch, nextState),
        MAPPING_COMPLETE_MAPPING: () => completeMapping(dispatch, nextState),
        MAPPING_CREATE_CUSTOM_SHELL: () => setShellDataFromMoviesAndProgramsData(dispatch, action),
        MAPPING_GO_TO_NEXT_MEDIAITEM: () => goToNextMediaItem(dispatch, action, nextState),
        MAPPING_POST_FULFILLED: () => handleMappingComplete(dispatch, nextState),
        MAPPING_REPLACE_DATA: () => handleMappingDataUpdate(dispatch, action, state, nextState),
        MAPPING_RESET: () => resetMapping(dispatch, action, nextState),
        MAPPING_SET_ACTIVE_TAB: () => handleSelectTab(dispatch, action, nextState),
        MAPPING_SET_MAPPING_DATA: () => handleMappingDataUpdate(dispatch, action, state, nextState),
        MAPPING_SET_BULK_MAPPING: () => handleSetBulkMapping(dispatch, action, nextState),
        MAPPING_SET_MAPPING_DATA_FROM_SHELL: () => setMappingDataFromShellData(dispatch, nextState, action),
        MAPPING_SET_MASS_SHELL: () => handleSetMassShell(dispatch, action, nextState),
        MAPPING_SET_MASS_SHELL_ACTIVE: () => handleSetMassShellActive(dispatch, action, nextState),
        MAPPING_SET_SHELL_CONTENT_TYPE: () => setMassShell(dispatch, nextState),
        MAPPING_SKIP_MASS_MAPPING: () => skipMassMapping(dispatch, nextState),
        MAPPING_SUBMIT_POST: () => postMappingData(dispatch, action, nextState),
        MAPPING_BULK_MAP: () => handleBulkMapping(dispatch, nextState),
        MASS_ACTION_TARGETS_UPDATE: () => setMassShell(dispatch, nextState),
        ROUTER_UPDATE: () => handleRouterUpdate(dispatch, action, nextState),
        SEARCH_DATA_FETCH_FULFILLED: () => updateMappingData(dispatch, action, state, nextState),
    };

    function handleFormRegister(dispatch, formName, {massActionData} = {}) {
        if (formName === 'bulkMapping' && massActionData) {
            const {targets = []} = massActionData;

            // when you change filter info, you also need to change the same ones in bulk-mapping-table.js
            dispatch({
                type: 'FORM_SET_FIELD_VALUE',
                formName,
                fieldName: 'skippedGuids',
                value: map(
                    filter(
                        targets,
                        (dataRow) => !isEmpty(
                                filter(
                                    ['description', 'duration', 'genres', 'rating', 'title'],
                                    (key) => isEmpty(dataRow) || !dataRow[key] || isEmpty(dataRow.genres)
                                )
                            )
                    ),
                    'guid',
                ),
            });
        }
    }

    if (actions[action.type]) {
        return actions[action.type]();
    }
};

function resetMapping(dispatch, {fullReset}, nextState) {
    if (fullReset) {
        batch(() => {
            dispatch({
                type: 'SEARCH_DATA_SET_FILTER',
                targets: undefined,
                activeId: 'guid',
                servicesDataKey: 'mediaItems',
            });

            dispatch({
                type: 'SEARCH_DATA_SET_FILTER',
                targets: undefined,
                activeId: 'guid',
                servicesDataKey: 'unmappedMediaItems',
            });

            dispatch({
                type: 'FORM_SET_FIELD_VALUE',
                fieldName: 'echostarContentType',
                formName: 'createShell',
                value: undefined,
            });
        });
    }

    dispatch({
        type: 'FORM_SET_FIELD_VALUE',
        fieldName: 'images',
        formName: 'createShell',
        value: undefined,
    });

    dispatch({
        type: 'FORM_SET_FIELD_VALUE',
        fieldName: 'extra',
        formName: 'createShell',
        value: undefined,
    });

    dispatch({
        type: 'FORM_SET_FIELD_VALUE',
        fieldName: 'credits',
        formName: 'createShell',
        value: undefined,
    });

    setMassShell(dispatch, nextState);
}

function cancelMapping(dispatch, nextState) {
    const {
        mapping: {bulkMapping, bulkMappingType, massMapping, massShell} = {},
        servicesSearchData: {
            mediaItems: {searchData: {content: mediaItemsContent} = {}} = {},
            unmappedMediaItems: {searchData: {content: unmappedMediaItemsContent} = {}} = {},
        } = {},
    } = nextState;

    if (massMapping || massShell || bulkMapping) {
        if (mediaItemsContent) {
            dispatch({
                type: 'SEARCH_DATA_REFRESH',
                actionShouldFetch: true,
                servicesDataKey: 'mediaItems',
                selectRecord: false,
            });
        }

        if (unmappedMediaItemsContent) {
            dispatch({
                type: 'SEARCH_DATA_REFRESH',
                actionShouldFetch: true,
                servicesDataKey: 'unmappedMediaItems',
                selectRecord: false,
            });
        }

        dispatch({
            type: 'MASS_ACTION_RESET',
        });

        if (bulkMapping) {
            if (bulkMappingType === 'SHOWS') {
                batch(() => {
                    dispatch({type: 'FORM_RESET', formName: 'bestMatches', submitted: true, resetValues: true});

                    dispatch({
                        type: 'SEARCH_DATA_SORT_RESET',
                        servicesDataKey: 'bestMatches',
                        clearSort: false,
                        actionShouldFetch: true,
                    });

                    dispatch({
                        type: 'FORM_RESET',
                        formName: 'moviesAndProgramsMappingFranchise',
                        submitted: true,
                        resetValues: true,
                    });

                    dispatch({
                        type: 'SEARCH_DATA_SORT_RESET',
                        servicesDataKey: 'moviesAndProgramsMappingFranchise',
                        clearSort: false,
                        actionShouldFetch: true,
                    });
                });
            }

            dispatch({
                type: 'FORM_RESET',
                formName: 'bulkMapping',
                submitted: true,
                resetValues: true,
            });
        }
    }

    dispatch({
        type: 'MAPPING_RESET',
        fullReset: true,
    });
}

function goToNextMediaItem(dispatch, {refreshServiceDataKey}, nextState) {
    const {
        mapping: {massMapping, massShell} = {},
        servicesSearchData: {
            mediaItems: {filter: {targets: mediaItemsFilter} = {}} = {},
            unmappedMediaItems: {filter: {targets: unmappedMediaItemsFilter} = {}} = {},
            [refreshServiceDataKey]: {searchData: {content: refreshServiceContent} = {}} = {},
        } = {},
        router: {routerParams: {guid: routerGuid} = {}} = {},
    } = nextState;

    if (massMapping || massShell) {
        const currentFilter = unmappedMediaItemsFilter || mediaItemsFilter;
        const currentServiceName = unmappedMediaItemsFilter ? 'unmappedMediaItems' : 'mediaItems';
        const currentIndex = findIndex(currentFilter, (guid) => guid === routerGuid);
        const nextFilter = filter(currentFilter, (guid) => guid !== routerGuid);

        if (!isEmpty(nextFilter)) {
            const nextTarget = currentIndex > -1 ? get(nextFilter, currentIndex) : undefined;
            const nextGuid = nextTarget || first(nextFilter);

            dispatch({
                type: 'SEARCH_DATA_SET_FILTER',
                targets: nextFilter,
                activeId: 'guid',
                servicesDataKey: currentServiceName,
            });

            dispatch(push(`/media-items/${nextGuid}`));

            if (size(nextFilter) === 1) {
                dispatch({
                    type: 'MAPPING_SET_LAST_MASS_ITEM',
                });
            } else {
                dispatch({
                    type: 'MAPPING_SET_LAST_MASS_ITEM',
                    lastMassItem: false,
                });
            }
        } else {
            dispatch({
                type: 'MAPPING_CANCEL_MAPPING',
            });
        }
    } else if (refreshServiceContent) {
        dispatch({
            type: 'SEARCH_DATA_REFRESH',
            actionShouldFetch: true,
            servicesDataKey: refreshServiceDataKey,
            selectRecord: true,
        });
    }
}

function skipMassMapping(dispatch, nextState) {
    const {
        servicesSearchData: {
            mediaItems: {filter: {targets: mediaItemsFilter} = {}} = {},
            unmappedMediaItems: {filter: {targets: unmappedMediaItemsFilter} = {}} = {},
        } = {},
        router: {routerParams: {guid: routerGuid} = {}} = {},
    } = nextState;
    const currentFilter = mediaItemsFilter || unmappedMediaItemsFilter;
    const currentIndex = findIndex(currentFilter, (guid) => guid === routerGuid);
    const nextFilter = filter(currentFilter, (guid) => guid !== routerGuid);

    if (!isEmpty(nextFilter)) {
        const nextTarget = currentIndex > -1 ? get(nextFilter, currentIndex) : undefined;
        const nextGuid = nextTarget || first(nextFilter);

        dispatch(push(`/media-items/${nextGuid}`));
    } else {
        dispatch({
            type: 'MAPPING_CANCEL_MAPPING',
        });
    }
}

function postMappingData(dispatch, {query}, nextState) {
    const {mapping: {massShell, massShellActive} = {}, router: {routerParams: {guid: routerGuid = ''} = {}} = {}} =        nextState;

    set(query, 'mediaItemGuid', routerGuid);

    const dispatchFetched = (jsonResponse) => {
        dispatch({
            type: 'MAPPING_POST_FULFILLED',
            formName: 'mapMediaItem',
            response: jsonResponse,
        });
    };
    const dispatchError = (error) => {
        dispatch({
            type: 'MAPPING_POST_REJECTED',
            error,
        });
    };
    const actionParams = {
        servicesConfig: servicesFormConfig.mapMediaItem,
        dispatchFetched,
        dispatchError,
    };

    dispatch({
        type: 'MAPPING_POST',
    });

    if (massShell && !massShellActive) {
        dispatch({
            type: 'MAPPING_SET_MASS_SHELL_ACTIVE',
            massShellActive: true,
        });
    }

    return Promise.resolve(actionsServiceCaller(actionParams, query));
}

function handleRouterUpdate(dispatch, {routerParams: {guid: routerGuid} = {}}, nextState) {
    const {
        servicesSearchData: {mediaItemDetail: {searchData: {content: [{guid: currentGuid} = {}] = []} = {}} = {}} = {},
    } = nextState;

    if (currentGuid && routerGuid && currentGuid !== routerGuid) {
        dispatch({
            type: 'MAPPING_RESET',
            fullReset: false,
        });
    }
}

function updateMappingData(
    dispatch,
    {servicesDataKey, searchData: {content: [newMediaItemData = {}] = []} = {}},
    state,
    nextState
) {
    if (servicesDataKey === 'mediaItemDetail') {
        const {mapping: {mediaItemData: {guid, lastUpdated} = {}} = {}} = state;
        const {guid: newGuid, lastUpdated: newLastUpdated} = newMediaItemData;

        if (guid === newGuid && lastUpdated !== newLastUpdated) {
            // Copy over echostarId from mediaItemData to mappingData if not present
            const {mapping: {fetched, showMessage, mappingData, mediaItemData} = {}} = nextState;

            if (fetched && showMessage) {
                const newMappingData = {};
                const {episode, franchise} = mappingData;
                const {amsEpisode, amsFranchise} = mediaItemData;

                if (franchise && amsFranchise) {
                    const {echostarId} = amsFranchise;

                    if (franchise.title && !franchise.echostarId && echostarId) {
                        set(newMappingData, 'franchise', {...franchise, echostarId});
                    }
                }

                if (episode && amsEpisode) {
                    const {echostarId} = amsEpisode;

                    if (episode.title && !episode.echostarId && echostarId) {
                        set(newMappingData, 'episode', {...episode, echostarId});
                    }
                }

                if (!isEmpty(newMappingData)) {
                    dispatch({
                        type: 'MAPPING_SET_MAPPING_DATA',
                        mappingData: newMappingData,
                        replaceMappingData: false,
                        showMessage: true,
                    });
                }
            }
        }

        setShellDataFromMediaItem(dispatch, newMediaItemData, nextState);
    }
}

function setShellDataFromMediaItem(dispatch, mediaItemData, nextState) {
    const {mapping: {mappingData: {franchise = {}, franchise: {echostarContentType} = {}} = {}, massShellActive} = {}} =        nextState;
    const {
        description,
        duration,
        episodeNumber,
        genres,
        lang,
        originalAirDate,
        rating,
        releaseYear,
        seasonNumber,
        episodeName,
        showName,
        title,
    } = mediaItemData;
    const {
        genres: franchiseGenres,
        rating: franchiseRating,
        releaseYear: franchiseReleaseYear,
        title: franchiseTitle,
    } = echostarContentType === 'SRM' ? franchise : {};

    const genresRevised = isArray(genres) && !isEmpty(genres) ? map(genres, 'name') : franchiseGenres || [];
    if (Config.base.matureContent === true && !genresRevised.includes('adult') && !genresRevised.includes('Adult')) {
        genresRevised.push('adult');
    }

    const shellData = {
        description,
        descriptionLanguage: lang || 'en',
        echostarContentType: echostarContentType === 'SRM' ? 'SRE' : massShellActive ? echostarContentType : undefined,
        episodeNumber: +episodeNumber,
        episodeSeason: +seasonNumber,
        genres: genresRevised,
        originalAirDate: formatDate(originalAirDate, 'YYYY-MM-DD'),
        rating: rating ? trim(rating.split('-').join('')).toUpperCase() : franchiseRating,
        releaseYear: releaseYear || franchiseReleaseYear,
        runtime: formatDuration(duration),
        seriesTitle: franchiseTitle || showName || title,
        title: episodeName || title,
        titleLanguages: lang || 'en',
    };

    updateShellFormValues(dispatch, shellData);
}

function setShellDataFromMoviesAndProgramsData(dispatch, {moviesAndProgramsData}) {
    const {
        description,
        descriptionLanguage,
        echostarContentType,
        episodeNumber,
        episodeSeason,
        images,
        credits,
        extra,
        genres,
        originalAirDate,
        rating,
        releaseYear,
        runtime,
        seriesTitle,
        shortDescription,
        title,
        titleLanguages,
    } = moviesAndProgramsData;

    const shellData = {
        description,
        shortDescription,
        descriptionLanguage: descriptionLanguage || 'en',
        echostarContentType,
        images,
        credits,
        extra,
        genres,
        originalAirDate: formatDate(originalAirDate, 'YYYY-MM-DD'),
        releaseYear,
        runtime: formatDuration(runtime),
        seriesTitle,
        title,
        titleLanguages: titleLanguages || 'en',
        episodeNumber: +episodeNumber,
        episodeSeason: +episodeSeason,
        rating: rating ? trim(rating.split('-').join('')).toUpperCase() : '',
    };

    updateShellFormValues(dispatch, shellData);
    setShellContentType(dispatch, echostarContentType);
}

function handleMappingDataUpdate(dispatch, {mappingData}, state, nextState) {
    const {mapping: {mappingData: {franchise: {echostarContentType} = {}} = {}} = {}} = state;
    const {
        mapping: {bulkMapping, mappingData: {franchise: {echostarContentType: nextEchostarContentType} = {}} = {}} = {},
    } = nextState;

    if ((echostarContentType === 'SRM') !== (nextEchostarContentType === 'SRM')) {
        dispatch({
            type: 'FORM_SET_FIELD_VALUE',
            fieldName: 'echostarContentType',
            formName: 'createShell',
            value: nextEchostarContentType === 'SRM' ? 'SRE' : undefined,
        });
    }

    if (!bulkMapping) {
        updateMassMapping(dispatch, mappingData, state, nextState);
    }
}

function updateMassMapping(dispatch, mappingData = {}, state, nextState) {
    const {
        mapping: {fetched, massMapping} = {},
        massActionData: {active, massActionName, targets} = {},
        router: {routerParams: {guid: routerGuid} = {}} = {},
    } = nextState;
    const {servicesSearchData: {[massActionName]: {searchData: {content: massActionContent = []} = {}} = {}} = {}} =        nextState;
    const {mapping: {mappingData: {franchise: currentFranchise} = {}} = {}} = state;
    const {episode, franchise} = mappingData;
    const {echostarContentType} = franchise || {};

    if (!currentFranchise && franchise) {
        if (!fetched && !massMapping && echostarContentType === 'SRM' && !episode) {
            if (active && !isEmpty(targets)) {
                const sortedItems = sortBy(map(targets, 'guid'), (guid) =>
                    findIndex(massActionContent, ['guid', guid])
                );

                Promise.all([
                    dispatch({
                        type: 'MAPPING_SET_MASS_MAPPING',
                        massMapping: true,
                    }),
                    dispatch({
                        type: 'SEARCH_DATA_SET_FILTER',
                        targets: sortedItems,
                        activeId: 'guid',
                        servicesDataKey: massActionName,
                    }),
                ]).then(() => {
                    if (!includes(sortedItems, routerGuid)) {
                        dispatch(push(`/media-items/${first(sortedItems)}`));
                    }
                });
            }
        }
    }
}

function setMediaItemsFilter(dispatch, nextState) {
    const {
        massActionData: {active, massActionName, targets} = {},
        router: {routerParams: {guid: routerGuid} = {}} = {},
    } = nextState;
    const {servicesSearchData: {[massActionName]: {searchData: {content: massActionContent = []} = {}} = {}} = {}} =        nextState;

    if (active && !isEmpty(targets)) {
        const sortedItems = sortBy(map(targets, 'guid'), (guid) => findIndex(massActionContent, ['guid', guid]));

        Promise.resolve(
            dispatch({
                type: 'SEARCH_DATA_SET_FILTER',
                targets: sortedItems,
                activeId: 'guid',
                servicesDataKey: massActionName,
            }),
        ).then(() => {
            if (!includes(sortedItems, routerGuid)) {
                dispatch(push(`/media-items/${first(sortedItems)}`));
            }
        });
    }
}

function handleFormSubmitted(dispatch, {formName, submitted}, nextState) {
    if (formName === 'createShell' && submitted) {
        setMappingDataFromShellData(dispatch, nextState);

        dispatch({
            type: 'FORM_SUBMITTED',
            actionShouldFetch: false,
            formName,
            servicesDataKey: formName,
            submitted: false,
        });
    }
}

function setMappingDataFromShellData(dispatch, nextState, {replaceMappingData, showMessage} = {}) {
    const {
        formData: {
            createShell: {
                values: {
                    description,
                    descriptionLanguage,
                    echostarContentType,
                    episodeNumber,
                    episodeSeason,
                    images,
                    credits,
                    extra,
                    genres,
                    originalAirDate,
                    rating,
                    releaseYear,
                    runtime,
                    seriesId,
                    seriesTitle,
                    shortDescription,
                    title,
                    titleLanguages,
                } = {},
            } = {},
        } = {},
        router: {routerParams: {guid: routerGuid} = {}} = {},
    } = nextState;
    const contentType = echostarContentType || 'MOV';
    const contentTypeKey = get(echostarContentTypeMap, `${contentType}.type`) || 'franchise';
    const mappingData = {
        mediaItemGuid: routerGuid,
        [contentTypeKey]: {
            description,
            descriptionLanguage,
            echostarContentType,
            images,
            credits,
            extra,
            genres,
            originalAirDate,
            rating,
            releaseYear,
            runtime: parseDurationAs(runtime, 'seconds'),
            seriesId,
            seriesTitle: seriesTitle || title,
            shortDescription,
            title,
            titleLanguages,
        },
    };

    if (contentTypeKey !== 'franchise') {
        set(mappingData, [contentTypeKey, 'episodeNumber'], episodeNumber);
        set(mappingData, [contentTypeKey, 'episodeSeason'], episodeSeason);
    }

    dispatch({
        type: 'MAPPING_SET_MAPPING_DATA',
        mappingData,
        replaceMappingData: replaceMappingData !== undefined ? replaceMappingData : false,
        showMessage: showMessage !== undefined ? showMessage : echostarContentType !== 'SRM',
    });
}

function updateShellFormValues(dispatch, shellData) {
    dispatch({
        type: 'FORM_SET_FIELD_VALUES',
        formName: 'createShell',
        resetValues: false,
        values: shellData,
    });
}

function handleFormFieldUpdate(dispatch, {fieldName, formName, value}, nextState) {
    if (formName === 'createShell') {
        if (fieldName === 'echostarContentType') {
            setShellTitle(dispatch, nextState);
            setShellContentType(dispatch, value);
        }
    }
}

function setShellContentType(dispatch, shellContentType) {
    dispatch({
        type: 'MAPPING_SET_SHELL_CONTENT_TYPE',
        shellContentType,
    });
}

function setShellTitle(dispatch, nextState) {
    const {
        formData: {createShell: {values: {echostarContentType} = {}} = {}} = {},
        mapping: {mediaItemData: {episodeName, showName, title} = {}} = {},
    } = nextState;
    const newTitle = echostarContentType === 'SRM' ? showName || title : episodeName || title;

    dispatch({
        type: 'FORM_SET_FIELD_VALUE',
        fieldName: 'title',
        formName: 'createShell',
        value: newTitle,
    });
}

function setMassShell(dispatch, nextState) {
    const {
        formData: {createShell: {values: {echostarContentType} = {}} = {}} = {},
        mapping: {
            activeTab,
            bulkMapping,
            mappingData: {franchise: {echostarContentType: mappingEchostarContentType} = {}} = {},
            massMapping,
            massShell,
            massShellActive,
        } = {},
        massActionData: {active, targets} = {},
    } = nextState;

    if (
        !massMapping
        && !bulkMapping
        && echostarContentType !== 'SRM'
        && active
        && !isEmpty(targets)
        && (!activeTab || activeTab === 'CREATESHELL')
        && mappingEchostarContentType !== 'SRM'
    ) {
        if (!massShellActive) {
            if (size(targets) === 1) {
                dispatch({
                    type: 'MAPPING_SET_LAST_MASS_ITEM',
                });
            } else {
                dispatch({
                    type: 'MAPPING_SET_LAST_MASS_ITEM',
                    lastMassItem: false,
                });
            }
        }

        if (!massShell) {
            dispatch({
                type: 'MAPPING_SET_MASS_SHELL',
                massShell: true,
            });
        }
    } else if (massShell) {
        dispatch({
            type: 'MAPPING_SET_MASS_SHELL',
            massShell: false,
        });
    }
}

function validateForm(dispatch, {formName}, nextState) {
    const {formData: {createShell: createShellFormData} = {}} = nextState;

    if (formName === 'createShell' && createShellFormData) {
        const {fieldErrors} = createShellFormData;

        if (isEmpty(fieldErrors)) {
            dispatch({
                type: 'MAPPING_SET_VALID_SHELL',
            });
        }
    }
}

function handleSetMassShell(dispatch, {massShell}, nextState) {
    if (massShell) {
        setMappingDataFromShellData(dispatch, nextState, {
            replaceMappingData: true,
            showMessage: false,
        });
    } else {
        dispatch({
            type: 'MAPPING_SET_MAPPING_DATA',
            mappingData: undefined,
            replaceMappingData: true,
            showMessage: false,
        });
    }
}

function handleSetMassShellActive(dispatch, {massShellActive}, nextState) {
    if (massShellActive) {
        setMediaItemsFilter(dispatch, nextState);
    }
}

function handleLayoutUpdate(dispatch, {activeTabId}) {
    if (includes(mappingTabNames, activeTabId)) {
        dispatch({
            type: 'MAPPING_SET_ACTIVE_TAB',
            activeTab: activeTabId,
        });
    }
}

function handleMappingComplete(dispatch, nextState) {
    const {mapping: {massShellActive} = {}} = nextState;

    if (massShellActive) {
        goToNextMediaItem(dispatch, {}, nextState);
    }
}

function handleSelectTab(dispatch, {activeTab}, nextState) {
    const {mapping: {massShellActive} = {}} = nextState;

    if (activeTab === 'CREATESHELL' || !massShellActive) {
        setMassShell(dispatch, nextState);
    }
}

function formatMappingReplayCriteria({mediaItemData, rememberMappingData, showRememberMapping}) {
    return showRememberMapping
        ? pickBy(mediaItemData, (value, key) => value && get(rememberMappingData, key))
        : undefined;
}

function formatMappingData(data) {
    return data
        ? pick(
              data,
              data.echostarId || data.roviProgramId || data.tmsId || data.sourceId
                  ? ['echostarId', 'roviProgramId', 'tmsId', 'sourceId']
                  : [
                        'description',
                        'descriptionLanguage',
                        'echostarContentType',
                        'episodeNumber',
                        'images',
                        'credits',
                        'extra',
                        'episodeSeason',
                        'genres',
                        'gnSeriesId',
                        'originalAirDate',
                        'rating',
                        'releaseYear',
                        'runtime',
                        'seriesId',
                        'seriesTitle',
                        'shortDescription',
                        'title',
                        'titleLanguages',
                    ],
          )
        : undefined;
}

function shouldFullyMap({
    mappingData: {episode, franchise: {echostarContentType: franchiseEchostarContentType} = {}} = {},
    massMapping,
    massShell,
}) {
    return massShell || !!(!episode && franchiseEchostarContentType === 'SRM' && !massMapping);
}

function completeMapping(dispatch, nextState) {
    const {
        mapping,
        mapping: {
            flaggedRoviProgramId: {episode: flaggedEpisodeRoviProgramId, franchise: flaggedFranchiseRoviProgramId} = {},
            flaggedTmsId: {episode: flaggedEpisodeTmsId, franchise: flaggedFranchiseTmsId} = {},
            mappingData: {episode, franchise} = {},
        } = {},
    } = nextState;

    const {targets} = nextState?.massActionData || {targets: []};
    if (targets?.length) {
        dispatch({
            type: 'ADD_MASS_GUID_FOR_GRACE_NOTE',
            guids: targets.map((t) => t?.guid),
        });
    }

    return Promise.resolve(
        dispatch({
            type: 'MAPPING_SUBMIT_POST',
            query: {
                episode: formatMappingData(episode),
                franchise: formatMappingData(franchise),
                fullyMapped: shouldFullyMap(mapping),
                mappingReplayCriteria: formatMappingReplayCriteria(mapping),
                flaggedFranchiseRoviProgramId,
                flaggedEpisodeRoviProgramId,
                flaggedFranchiseTmsId,
                flaggedEpisodeTmsId,
            },
        }),
    );
}

function handleSetBulkMapping(dispatch, {bulkMapping, bulkMappingType}, nextState) {
    const {
        mapping: {massShell},
    } = nextState;

    if (bulkMapping) {
        if (massShell) {
            dispatch({
                type: 'MAPPING_SET_MASS_SHELL',
                massShell: false,
            });
        }

        dispatch({
            type: 'MAPPING_SET_SHOW_MESSAGE',
            showMessage: false,
        });

        setMediaItemsFilter(dispatch, nextState);

        if (bulkMappingType === 'SHOWS') {
            batch(() => {
                dispatch({
                    type: 'FORM_SET_FIELD_VALUE',
                    fieldName: 'typeFilter',
                    formName: 'bestMatches',
                    value: 'shows_only',
                });

                dispatch({type: 'SEARCH_DATA_SEARCH', servicesDataKey: 'bestMatches'});

                dispatch({
                    type: 'FORM_SET_FIELD_VALUE',
                    fieldName: 'typeFilter',
                    formName: 'moviesAndProgramsMappingFranchise',
                    value: 'shows_only',
                });

                dispatch({type: 'SEARCH_DATA_SEARCH', servicesDataKey: 'moviesAndProgramsMappingFranchise'});

                dispatch({
                    type: 'FORM_SET_FIELD_VALUE',
                    fieldName: 'echostarContentType',
                    formName: 'createShell',
                    value: 'SRM',
                });
            });
        }
    }
}

function handleBulkMapping(dispatch, nextState) {
    const {
        formData: {bulkMapping: {values: {skippedGuids = []} = {}} = {}} = {},
        massActionData: {childFormName: formName, targets} = {},
        mapping: {mappingData: {franchise = {}} = {}} = {},
    } = nextState;
    const validTargets = filter(targets, (target) => !skippedGuids.includes(target.guid));
    const bulkMappingData = map(validTargets, (target) => createBulkMappingRequestFromMediaItem(target, franchise));

    if (formName && !isEmpty(bulkMappingData)) {
        const showsOrSpecialPayload =            formName === 'bulkMapSpecials'
                ? {
                      mappingRequests: bulkMappingData,
                  }
                : formName === 'bulkMapShows'
                  ? {
                        franchise: formatMappingData(franchise),
                        mappingRequests: bulkMappingData,
                    }
                  : {};

        Promise.resolve(
            dispatch({
                formName,
                resetValues: true,
                type: 'FORM_SET_FIELD_VALUES',
                values: showsOrSpecialPayload,
            }),
        ).then(() => {
            Promise.resolve(
                dispatch({
                    type: 'FORM_SUBMIT_FETCH',
                    formName,
                    async: false,
                }),
            );
        });
    }
}

function createBulkMappingRequestFromMediaItem(mediaItemData, franchise) {
    const {
        description,
        duration,
        episodeNumber,
        genres,
        guid,
        lang,
        originalAirDate,
        rating,
        releaseYear,
        seasonNumber,
        episodeName,
        showName,
        title,
    } = mediaItemData;
    const {
        echostarContentType = '',
        genres: franchiseGenres,
        rating: franchiseRating,
        releaseYear: franchiseReleaseYear,
        title: franchiseTitle,
    } = franchise;
    const shellData = {
        description,
        descriptionLanguage: lang || 'N/A',
        echostarContentType: echostarContentType === 'SRM' ? 'SRE' : 'SPM',
        episodeNumber: !isEmpty(franchise) ? +episodeNumber : undefined,
        episodeSeason: !isEmpty(franchise) ? +seasonNumber : undefined,
        genres: isArray(genres) && !isEmpty(genres) ? map(genres, 'name') : franchiseGenres || [],
        originalAirDate: formatDate(originalAirDate, 'YYYY-MM-DD'),
        rating: rating ? trim(rating.split('-').join('')).toUpperCase() : franchiseRating,
        releaseYear: releaseYear || franchiseReleaseYear,
        runtime: duration,
        seriesTitle: franchiseTitle || showName || title,
        title: episodeName || title,
        titleLanguages: lang || 'N/A',
    };
    const bulkMappingRequest = {
        mediaItemGuid: guid,
        episode: !isEmpty(franchise) && echostarContentType === 'SRM' ? shellData : undefined,
        franchise: isEmpty(franchise) ? shellData : undefined,
        fullyMapped: false,
    };

    return bulkMappingRequest;
}

export default mappingMiddleware;
